import { Cancel, Check } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    TextField,
    Button,
    DialogProps
} from '@mui/material';
import { AxiosError } from 'axios';
import React from 'react';
import { toast } from 'react-toastify';
import { useNotificationsState } from '../../../lib/contexts/NotificationsContext';
import { ErrorResponse } from '../../../lib/dtos/ErrorResponse';
import { formatPhoneNumber } from '../../../lib/helper/format-phone-number';
import { StyledDialogActions } from '../../../styledComponents';

export type EditPhoneNumberNotificationModalProps = Pick<DialogProps, 'open'> & {
    value: string;
    onClose: (
        event: unknown,
        reason: 'backdropClick' | 'escapeKeyDown' | 'clickCancelButton' | 'clickSubmitButton'
    ) => void;
    onSubmit: (value: string) => void | Promise<void>;
};

const EditPhoneNumberNotificationModal = ({
    open,
    value,
    onClose,
    onSubmit
}: EditPhoneNumberNotificationModalProps) => {
    const [resolved, setResolved] = React.useState(true);
    const [inputValue, setInputValue] = React.useState('');
    const [shownValue, setShownValue] = React.useState(value);

    const context = useNotificationsState();

    const onSubmitHandler: React.FormEventHandler = async (e) => {
        e.preventDefault();

        if (inputValue.length < 10) {
            if (!(inputValue.length <= 0)) {
                toast.error('Phone number must be exactly 10 characters long');
                return;
            }
        }

        setResolved(false);

        try {
            await onSubmit.call(null, inputValue);
            onClose({}, 'clickSubmitButton');
            toast.success('Successfully updated', { autoClose: 5000 });
        } catch (ex) {
            const error = ex as AxiosError<ErrorResponse>;
            onClose({}, 'clickSubmitButton');
            toast.error('Failed updating the field: ' + error.response?.data.message, {
                autoClose: 5000
            });
        } finally {
            setResolved(true);
        }
    };

    const clickCancelHandler = () => {
        onClose({}, 'clickCancelButton');
        setInputValue(value);
        setShownValue(formatPhoneNumber(value));
    };

    const inputChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setInputValue(e.target.value.replace(/[^\d]/g, '').substring(0, 10));
        setShownValue(formatPhoneNumber(e.target.value));
    };

    React.useEffect(() => {
        setShownValue(formatPhoneNumber(context.phoneNumber));
    }, [context.phoneNumber]);

    return (
        <Dialog open={open} onClose={onClose}>
            <form onSubmit={onSubmitHandler}>
                <DialogTitle sx={{ fontSize: 20, fontWeight: (t) => t.typography.fontWeightBold }}>
                    Text Notifications
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>Please enter your phone number</DialogContentText>
                    <TextField
                        margin="dense"
                        label="Phone Number"
                        type="text"
                        fullWidth
                        variant="standard"
                        onChange={inputChangeHandler}
                        value={shownValue}
                    />
                </DialogContent>
                <StyledDialogActions>
                    <LoadingButton
                        type="submit"
                        loading={!resolved}
                        loadingPosition="start"
                        variant="contained"
                        startIcon={<Check />}>
                        Save
                    </LoadingButton>
                    <Button startIcon={<Cancel />} variant="outlined" onClick={clickCancelHandler}>
                        Cancel
                    </Button>
                </StyledDialogActions>
            </form>
        </Dialog>
    );
};

export default EditPhoneNumberNotificationModal;
